import React, { useState, useContext, useRef, useEffect } from "react";
import { RadioButton } from "primereact/radiobutton";
import { AddPropertyContext } from "./AddPropertyContext";
import PurchaseDetails from "./PurchaseDetails";
import CurrentStatus from "./CurrentStatus";
import { Button } from "primereact/button";
import OtherPurchaseExpenses from "./OtherPurchaseExpenses";
import Loan from "./Loan";
import { formatCurrency } from "../../../utils/commonUtil";

interface PurchaseAndLoansProps {
  onNext: () => void;
  onBack: () => void;
  portfolioTrackerId?: number;
  isEditMode?: boolean;
  isUserDefault?: boolean;
}

const PurchaseAndLoans = ({ onNext, onBack, portfolioTrackerId, isEditMode, isUserDefault }: PurchaseAndLoansProps) => {
  const context = useContext(AddPropertyContext);
  const [purchaseType, setPurchaseType] = useState(isEditMode ? "existing" : "new");
  const purchaseDetailsRef = useRef<{ validate: () => boolean }>(null);
  const currentStatusRef = useRef<{ validate: () => boolean }>(null);
  const loanRef = useRef<{ validate: () => boolean }>(null);
  const [loanPurchaseCost, setLoanPurchaseCost] = useState(0);
  const [exceedAmount, setExceedAmount] = useState<number>(0);
  const [isExistingLoan, setIsExistingLoan] = useState<boolean>(true);
  const [userDefault, setUserDefault] = useState<boolean>(isUserDefault);
  const [defaultLoanPurchaseCost, setDefaultLoanPurchaseCost] = useState(0);

  if (!context) {
    return <div>Loading...</div>;
  }

  useEffect(() => {
    if (context.property && context.property.PurchaseType) {
      setIsExistingLoan(context.property.IsExistingLoan);
      if (context.property.PurchaseType !== purchaseType && !isEditMode) {
        setPurchaseType(context.property.PurchaseType);
      }
    }
  }, [context.property]);

  const handleLoanTypeChange = (value: boolean, index: number) => {
    setIsExistingLoan(value);
    const existingLoansLength = (context?.property?.ExistingLoans?.length ?? 0) - 1;
    const targetIdx = isEditMode ? index - existingLoansLength - 1 : index;
    if (!value && context.property && context.property.Loans && context.property.Loans.length > 0) {
      context.setProperty({
        ...context.property,
        AmountOwning: 0,
        IsExistingLoan: value,
        Loans: context.property.Loans.map((loan, idx) => {
          return idx === targetIdx ? { ...loan, Balance: 0, isNewLoan: 1 } : loan;
        }),
      });
    } else if (context.property && context.property.Loans && context.property.Loans.length > 0) {
      const loanPurchaseCost = calculateLoanPurchaseCost();
      context.setProperty({
        ...context.property,
        AmountOwning: loanPurchaseCost,
        IsExistingLoan: value,
        Loans: context.property.Loans.map((loan, idx) =>
          idx === targetIdx ? { ...loan, Balance: loanPurchaseCost, isNewLoan: 0 } : loan
        ),
      });
    }
  };

  useEffect(() => {
    if (context.property && !isEditMode && purchaseType === "existing") {
      const loanPurchaseCost = calculateLoanPurchaseCost();
      setLoanPurchaseCost(loanPurchaseCost);
    }
    if (context.property && context.property.Loans && context.property.Loans.length > 0 && !isEditMode) {
      context.setProperty({
        ...context.property,
        AmountOwning: calculateLoanPurchaseCost(),
      });
    }
  }, [purchaseType]);

  const calculateLoanPurchaseCost = () => {
    var loanPurchaseCost = 0;
    loanPurchaseCost =
      (context.property.Current_InitialPurchasePrice ?? 0) +
      (context.property.Cost_CapitalImprovements ?? 0) +
      (context.property.Cost_OtherCosts ?? 0) +
      (context.property.Cost_SolicitorFees ?? 0) +
      (context.property.Cost_StampDuty ?? 0);

    return loanPurchaseCost;
  };

  const storeLastLoanBalance = () => {
    if (!isEditMode && context.property && context.property.Loans && context.property.Loans.length > 0) {
      context.setProperty({
        ...context.property,
        LastLoanBalance: context.property.Loans[0]?.Balance ?? 0,
      });
    }
  };

  const handleNextClick = () => {
    if (purchaseType === "new" && !isEditMode) {
      const isPurchaseDetailsValid = purchaseDetailsRef.current?.validate();
      const isLoanValid = loanRef.current?.validate();
      if (isPurchaseDetailsValid && isLoanValid) {
        storeLastLoanBalance();
        onNext();
      }
    } else {
      const isLoanValid = loanRef.current?.validate();
      if (currentStatusRef.current?.validate() && isLoanValid) {
        storeLastLoanBalance();
        onNext();
      }
    }
  };

  useEffect(() => {
    if (
      context.property &&
      !isEditMode &&
      isExistingLoan &&
      purchaseType === "existing" &&
      context.property.Loans &&
      context.property.Loans[0] &&
      context.property.Loans.length > 0
    ) {
      setLoanPurchaseCost(context.property.AmountOwning ?? 0);
    }
  }, [context.property.AmountOwning]);

  useEffect(() => {
    if (context.property && context.property.PurchaseType == "new") {
      var totalPurchaseCost = 0;
      var initialLoanCosts = 0;
      initialLoanCosts = totalPurchaseCost =
        (context.property.PurchasePrice ?? 0) +
        (context.property.Cost_CapitalImprovements ?? 0) +
        (context.property.Cost_OtherCosts ?? 0) +
        (context.property.Cost_SolicitorFees ?? 0) +
        (context.property.Cost_StampDuty ?? 0);

      context.property.Loans?.forEach((loan) => {
        totalPurchaseCost +=
          parseFloat(loan.EstablishmentFees ?? 0) +
          parseFloat(loan.OtherLoanCost ?? 0) +
          parseFloat(loan.ValuationFees ?? 0);
      });

      initialLoanCosts +=
        parseFloat(context.property.Loans?.[0]?.EstablishmentFees ?? 0) +
        parseFloat(context.property.Loans?.[0]?.OtherLoanCost ?? 0) +
        parseFloat(context.property.Loans?.[0]?.ValuationFees ?? 0);

      setLoanPurchaseCost(totalPurchaseCost);
      setDefaultLoanPurchaseCost(initialLoanCosts);
    } else if (context.property && context.property.PurchaseType == "existing") {
      setLoanPurchaseCost(calculateLoanPurchaseCost());
    }
  }, [
    context.property.PurchaseType,
    context.property.PurchasePrice,
    context.property.Current_InitialPurchasePrice,
    context.property.Cost_CapitalImprovements,
    context.property.Cost_OtherCosts,
    context.property.Cost_SolicitorFees,
    context.property.Cost_StampDuty,
    context.property.Loans,
    context.userProfile,
  ]);

  useEffect(() => {
    if (
      context?.property &&
      (context?.property.LastLoanBalance || 0) > 0 &&
      context?.property.Loans &&
      context?.property.Loans.length > 0 &&
      !isEditMode
    ) {
      context.setProperty({
        ...context.property,
        Loans: context.property.Loans.map((loan, idx) =>
          idx === 0 ? { ...loan, Balance: context.property.LastLoanBalance || 0 } : loan
        ),
      });
      return;
    }

    const purchaseAmount =
      context.property.PurchaseType == "new"
        ? context.property.PurchasePrice
        : context.property.Current_InitialPurchasePrice;
    if (purchaseAmount === undefined || purchaseAmount === null || purchaseAmount === 0) {
      if (context.property.Loans && context.property.Loans[0]) {
        context.setProperty({
          ...context.property,
          Loans: context.property.Loans.map((loan, idx) => (idx === 0 ? { ...loan, Balance: 0 } : loan)),
        });
      }
    } else {
      const cashDeposit = context.property.DepositCash ?? 0;
      if (context.property.Loans && context.property.Loans[0]) {
        if (!context.property.Property_PurchaseType || context.property.Property_PurchaseType == "percentage") {
          context.setProperty({
            ...context.property,
            Loans: context.property.Loans.map((loan, idx) =>
              idx === 0
                ? { ...loan, Balance: Math.max(0, defaultLoanPurchaseCost - purchaseAmount * (cashDeposit / 100)) }
                : loan
            ),
          });
        } else {
          context.setProperty({
            ...context.property,
            Loans: context.property.Loans.map((loan, idx) =>
              idx === 0 ? { ...loan, Balance: Math.max(0, defaultLoanPurchaseCost - cashDeposit) } : loan
            ),
          });
        }
      }
    }
  }, [loanPurchaseCost, context.property.DepositCash]);

  useEffect(() => {
    const purchaseAmount = context.property.PurchasePrice;
    const cashDeposit = context.property.DepositCash ?? 0;
    var depositAmount = 0;
    if (cashDeposit <= 100) {
      depositAmount = (purchaseAmount ?? 0) * (cashDeposit / 100);
    } else {
      depositAmount = cashDeposit;
    }
    setExceedAmount(context.property.Loans?.[0]?.Balance + depositAmount - loanPurchaseCost);
  }, [context.property.Loans?.[0]?.Balance]);

  useEffect(() => {
    setUserDefault(isUserDefault);
  }, [isUserDefault]);

  return (
    <div className="p-fluid">
      {!isEditMode && (
        <div className="mb-4">
          <h3 className="text-xl font-bold mb-2">Is this a new purchase or an existing property you own?</h3>
          <div className="flex gap-4">
            <div className="flex items-center">
              <RadioButton
                inputId="newPurchase"
                data-testid="appl-rdo-newpurchase"
                name="purchaseType"
                value="new"
                onChange={(e) => {
                  setPurchaseType(e.value);
                  context.setProperty({ ...context.property, PurchaseType: e.value });
                }}
                checked={purchaseType === "new"}
              />
              <label htmlFor="newPurchase" className="ml-2">
                It's a new purchase
              </label>
            </div>
            <div className="flex items-center">
              <RadioButton
                inputId="existingProperty"
                data-testid="appl-rdo-existingproperty"
                name="purchaseType"
                value="existing"
                onChange={(e) => {
                  setPurchaseType(e.value);
                  context.setProperty({ ...context.property, PurchaseType: e.value });
                }}
                checked={purchaseType === "existing"}
              />
              <label htmlFor="existingProperty" className="ml-2">
                I already own it
              </label>
            </div>
          </div>
        </div>
      )}

      {purchaseType === "new" && !isEditMode ? (
        <>
          <PurchaseDetails ref={purchaseDetailsRef} onNext={onNext} onBack={onBack} />
        </>
      ) : (
        <CurrentStatus ref={currentStatusRef} isEditMode={isEditMode} />
      )}
      <OtherPurchaseExpenses isEditMode={isEditMode} isUserDefault={userDefault} />
      <Loan
        ref={loanRef}
        purchaseType={isEditMode ? "existing" : purchaseType}
        portfolioTrackerId={portfolioTrackerId}
        isEditMode={isEditMode ? true : false}
        isExistingLoan={isExistingLoan}
        onLoanTypeChange={handleLoanTypeChange}
        isUserDefault={userDefault}
      />
      {purchaseType === "new" ? (
        <div className="w-full">
          <div className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2">
            <div className="">
              <div className="flex flex-col lg:flex-row lg:w-full gap-2">
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  <span className="font-semibold">Total purchase costs:</span>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  <span>{formatCurrency(loanPurchaseCost)}</span>
                </div>
                <div className="flex-0 flex-col w-full lg:w-1/3 gap-2">
                  {exceedAmount > 0 && (
                    <span className="">
                      {`Deposit and loans are ${formatCurrency(exceedAmount)} more than Purchase Costs.`}
                    </span>
                  )}
                  {exceedAmount < 0 && (
                    <span className="">
                      {`Deposit and loans are ${formatCurrency(-exceedAmount)} less than Purchase Costs.`}
                    </span>
                  )}
                  {exceedAmount === 0 && <span className="">{`Deposit and loans are equal to Purchase Costs.`}</span>}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex justify-between mt-4">
        <div>
          <Button
            id="appl-btn-back"
            data-testid="appl-btn-back"
            label="Summary"
            icon="pi pi-arrow-left"
            onClick={onBack}
            className="p-button-secondary"
          />
        </div>
        <div>
          <Button
            id="appl-btn-next"
            data-testid="appl-btn-next"
            label="Rent & expenses"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNextClick}
          />
        </div>
      </div>
    </div>
  );
};

export default PurchaseAndLoans;
